import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LANGS = [{ value: "en", label: "English" }, { value: "it", label: "Italiano" }];
const FORMATS = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"];

export function CreateWorkspaceDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", company_name: "", country: "", default_language: "en", default_date_format: "DD/MM/YYYY" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Workspace name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", form);
      toast.success(`Workspace "${form.name}" created`);
      onOpenChange(false);
      setForm({ name: "", company_name: "", country: "", default_language: "en", default_date_format: "DD/MM/YYYY" });
      if (onCreated) onCreated(data.company_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-workspace-dialog">
        <DialogHeader>
          <DialogTitle>Create a new workspace</DialogTitle>
          <DialogDescription>You become its admin. It starts completely empty and isolated from your other workspaces.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Workspace name</Label>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="e.g. Northwind Projects" className="mt-1" data-testid="create-ws-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Company name (optional)</Label>
              <Input value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} className="mt-1" data-testid="create-ws-company" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Country (optional)</Label>
              <Input value={form.country} onChange={(e) => set("country")(e.target.value)} className="mt-1" data-testid="create-ws-country" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Default language</Label>
              <Select value={form.default_language} onValueChange={set("default_language")}>
                <SelectTrigger className="mt-1" data-testid="create-ws-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Default date format</Label>
              <Select value={form.default_date_format} onValueChange={set("default_date_format")}>
                <SelectTrigger className="mt-1" data-testid="create-ws-dateformat"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={submit} disabled={busy} data-testid="create-ws-submit">Create workspace</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
